import { useEffect, useRef } from "react";
import { RAIL_STEP_KEYS } from "../rail-model";
import {
  clientInitial,
  staffCurrentStep,
  staffStepStatus,
  staffStepStatusLabel
} from "./core";
import type { StaffRailStep } from "./model";

export type StaffStepVisual = "locked" | "next" | "working" | "done" | "blocked" | "done_ahead";

/**
 * Every canonical status keeps its OWN visual state. Mapping `blocked` onto
 * the calm orange "pracuje se" made nine team-owned blockages look like
 * background work in the matrix (audit a3-08); `done_ahead` painted as plain
 * done hid the anomaly the summary counts (a3-04).
 */
export function staffStepVisualState(step: StaffRailStep): StaffStepVisual {
  const status = staffStepStatus(step);
  if (status === "locked" || status === "done" || status === "working") return status;
  if (status === "blocked" || status === "done_ahead") return status;
  if (status === "testing" || status === "waiting_apple") return "working";
  return "next";
}

export function StaffClientAvatar(props: {
  name: string;
  email: string;
  photoUrl: string | null;
}) {
  return (
    <span
      className="matrix-client-photo matrix-client-initial"
      aria-hidden="true"
      style={{ position: "relative", overflow: "hidden" }}
    >
      {clientInitial(props.name, props.email)}
      {props.photoUrl && (
        <img
          alt=""
          className="matrix-client-photo"
          loading="lazy"
          onError={(event) => {
            event.currentTarget.style.display = "none";
          }}
          src={props.photoUrl}
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
        />
      )}
    </span>
  );
}

/**
 * The scrollLeft that centers the selected step inside the rail scroller,
 * clamped to the scrollable range. Pure numbers in, one number out, so the
 * regression test can replay the exact coordinates the audit measured.
 */
export function railCenterScrollLeft(metrics: {
  scrollLeft: number;
  scrollWidth: number;
  clientWidth: number;
  railLeft: number;
  railWidth: number;
  stepLeft: number;
  stepWidth: number;
}): number {
  const target = metrics.scrollLeft
    + (metrics.stepLeft - metrics.railLeft)
    - (metrics.railWidth - metrics.stepWidth) / 2;
  const max = Math.max(0, metrics.scrollWidth - metrics.clientWidth);
  return Math.max(0, Math.min(Math.round(target), max));
}

export function StaffJourneyRail(props: {
  steps: StaffRailStep[];
  selectedStep: string | null;
  t: (key: string) => string;
  onSelect: (step: StaffRailStep) => void;
}) {
  const railRef = useRef<HTMLElement | null>(null);
  const selectedStep = props.selectedStep;
  useEffect(() => {
    // Third rail repair (fixr5, audit a3-03/a2-03). fixr4 fired ONE centering
    // after two animation frames; the live probe caught the window where the
    // flex band is still laying out (scrollLeft=0, selected step at x=672,
    // the audit's exact numbers) and on a slow device the single shot can
    // even run before the band exists, when railCenterScrollLeft clamps the
    // target to max(0, scrollWidth-clientWidth)=0 and nothing ever corrects
    // it. Centering now re-runs whenever the rail's GEOMETRY actually
    // changes (ResizeObserver on the rail and its chapter sections, fonts
    // arriving, breakpoint flips), so it converges after real settling.
    // Resizes only - a user's manual scroll never re-triggers it.
    if (!selectedStep) return;
    const rail = railRef.current;
    if (!rail) return;
    const media = window.matchMedia("(max-width: 767px)");
    let frame = 0;
    const center = () => {
      if (!media.matches) return;
      const button = rail.querySelector<HTMLElement>(
        `[data-staff-step="${CSS.escape(selectedStep)}"]`
      );
      if (!button) return;
      const railRect = rail.getBoundingClientRect();
      const stepRect = button.getBoundingClientRect();
      rail.scrollLeft = railCenterScrollLeft({
        scrollLeft: rail.scrollLeft,
        scrollWidth: rail.scrollWidth,
        clientWidth: rail.clientWidth,
        railLeft: railRect.left,
        railWidth: railRect.width,
        stepLeft: stepRect.left,
        stepWidth: stepRect.width
      });
    };
    const schedule = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(center);
    };
    const observer = typeof ResizeObserver === "undefined"
      ? null
      : new ResizeObserver(schedule);
    observer?.observe(rail);
    for (const section of rail.querySelectorAll(":scope > section")) {
      observer?.observe(section);
    }
    document.fonts?.ready.then(schedule).catch(() => undefined);
    // 🔴 A frame is not guaranteed. A page that is HIDDEN serves no animation
    // frames and no ResizeObserver callbacks at all, so a rail centred only
    // from a frame never moves: measured on the deployed console in a hidden
    // tab (audit2 N05), three warm loads in a row ran this effect, created the
    // observer, requested the frame and got nothing - scrollLeft 0 with the
    // open step 2 369 px off screen, unchanged after six seconds; a single
    // forced frame then landed it on 2 299. So the rail is also centred from
    // the layout available RIGHT NOW, and again the moment the page comes
    // back to the front. The frame and the observers stay: they are what
    // corrects a measurement taken while the band was still settling.
    center();
    schedule();
    media.addEventListener("change", schedule);
    document.addEventListener("visibilitychange", schedule);
    return () => {
      window.cancelAnimationFrame(frame);
      observer?.disconnect();
      media.removeEventListener("change", schedule);
      document.removeEventListener("visibilitychange", schedule);
    };
  }, [selectedStep, props.steps]);
  return (
    <aside ref={railRef} className="staff-journey-rail" aria-label={props.t("partnerOS.rail.label")}>
      {(["buddy", "sales"] as const).map((chapter) => {
        const steps = props.steps.filter((step) => step.chapter === chapter);
        if (steps.length === 0) return null;
        return (
          <section data-step-chapter={chapter} key={chapter}>
            <h3>{props.t(`rail.chapters.${chapter}`)}</h3>
            <ol>
              {steps.map((step) => {
                const selected = props.selectedStep === step.key;
                return (
                  <li key={step.key}>
                    <button
                      aria-current={selected ? "step" : undefined}
                      className={`staff-journey-step ${staffStepVisualState(step)} status-${staffStepStatus(step)}${selected ? " selected" : ""}`}
                      data-staff-step={step.key}
                      onClick={() => props.onSelect(step)}
                      type="button"
                    >
                      <span className="staff-step-number step-number-token">
                        {step.position ?? RAIL_STEP_KEYS.indexOf(step.key) + 1}
                      </span>
                      <span className="staff-step-copy">
                        <strong>{props.t(step.labelKey)}</strong>
                        <small>{staffStepStatusLabel(step, props.t)}</small>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ol>
          </section>
        );
      })}
    </aside>
  );
}

/**
 * The legend is the written contract for the marks (fix-partak-stavy law), so
 * it has to list every mark the strip DRAWS. N09: the strip paints a seventh -
 * `.current`, scaled 1.35 with a ring, the loudest mark in the whole column -
 * and the legend explained six, leaving the partner to guess the biggest one.
 * `current` is a MODIFIER, not a state, so its sample carries the state it
 * rides on most often (`next`), exactly as the strip renders it.
 */
const STAFF_LEGEND_MARKS = [
  { key: "done", className: "done" },
  { key: "done_ahead", className: "done_ahead" },
  { key: "next", className: "next" },
  { key: "working", className: "working" },
  { key: "blocked", className: "blocked" },
  { key: "locked", className: "locked" },
  { key: "current", className: "next current" }
] as const;

export function StaffStepLegend(props: { t: (key: string) => string }) {
  return (
    <div className="staff-step-legend" aria-label={props.t("staffConsole.legend.title")}>
      {STAFF_LEGEND_MARKS.map((mark) => (
        <span key={mark.key}>
          <i className={`staff-step-mark ${mark.className}`} aria-hidden="true" />
          {props.t(`staffConsole.legend.${mark.key}`)}
        </span>
      ))}
    </div>
  );
}

export function StaffStepStrip(props: {
  steps: StaffRailStep[];
  t: (key: string) => string;
}) {
  // F126: the white ring is the JOURNEY's frontier, not the client's own
  // "next step" pointer - see staffCurrentStep. It used to read
  // `step.primaryNext`, which parks on the first step still on the client's
  // turn, so a client with an open beta never saw it move again.
  const current = staffCurrentStep(props.steps);
  return (
    <div
      className="staff-step-strip"
      aria-label={props.t("staffConsole.stepProgressAria")
        .replace("{count}", String(props.steps.length))}
    >
      {/* F143: a chapter this client does not walk is not labelled. The rail
          above already guarded; these two did not, so a sales_only client's
          strip would carry "Příprava Buddyho" over no marks at all. */}
      {(["buddy", "sales"] as const).filter(
        (chapter) => props.steps.some((step) => step.chapter === chapter)
      ).map((chapter) => (
        <div className="staff-step-strip-group" data-step-chapter={chapter} key={chapter}>
          <span className="staff-step-strip-label">
            {props.t(`staffConsole.stepChapter.${chapter}`)}
          </span>
          <span className="staff-step-marks" role="list">
            {props.steps.filter((step) => step.chapter === chapter).map((step) => (
              <span
                className={`staff-step-mark ${staffStepVisualState(step)}${step.key === current?.key ? " current" : ""}`}
                data-staff-step={step.key}
                key={step.key}
                role="listitem"
                title={`${props.t(step.labelKey)}: ${staffStepStatusLabel(step, props.t)}`}
              >
                <span className="visually-hidden">
                  {props.t(step.labelKey)}: {staffStepStatusLabel(step, props.t)}
                </span>
              </span>
            ))}
          </span>
        </div>
      ))}
    </div>
  );
}

export function StaffStepOverview(props: {
  steps: StaffRailStep[];
  t: (key: string) => string;
  onSelect?: (step: StaffRailStep) => void;
}) {
  const current = staffCurrentStep(props.steps);
  return (
    <article className="staff-card staff-step-overview">
      <h3>{props.t("staffConsole.stepOverview.title")}</h3>
      <p className="staff-card-hint">{props.t("staffConsole.stepOverview.hint")}</p>
      {current && (
        <p className="staff-step-next">
          {props.t("staffConsole.stepOverview.clientNext")
            .replace("{step}", props.t(current.labelKey))
            .replace("{state}", staffStepStatusLabel(current, props.t))}
        </p>
      )}
      <div className="staff-step-chapters">
        {(["buddy", "sales"] as const).filter(
          (chapter) => props.steps.some((step) => step.chapter === chapter)
        ).map((chapter) => (
          <section data-step-chapter={chapter} key={chapter}>
            <h4>{props.t(`rail.chapters.${chapter}`)}</h4>
            <ol>
              {props.steps.filter((step) => step.chapter === chapter).map((step) => (
                <li
                  className={`staff-step-item ${staffStepVisualState(step)} status-${staffStepStatus(step)}${step.key === current?.key ? " current" : ""}`}
                  data-staff-step={step.key}
                  key={step.key}
                >
                  <button
                    className="staff-step-overview-button"
                    onClick={() => props.onSelect?.(step)}
                    type="button"
                  >
                    <span className="staff-step-number step-number-token">
                      {step.position ?? RAIL_STEP_KEYS.indexOf(step.key) + 1}
                    </span>
                    <span className="staff-step-copy">
                      <strong>{props.t(step.labelKey)}</strong>
                      <small>{staffStepStatusLabel(step, props.t)}</small>
                    </span>
                  </button>
                </li>
              ))}
            </ol>
          </section>
        ))}
      </div>
    </article>
  );
}
